/**
 * OrderConfirmationPage - Page de confirmation après commande
 * Affiche un récapitulatif complet imprimable de la commande
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCustomer } from '../../contexts/CustomerContext';
import { ecommerceApi } from '../../api/ecommerce.api';
import type { Order } from '../../types/ecommerce.types';
import { formatJourRetrait, formatPlageHoraire, lieuDeRetrait } from '../../utils/shopOrdering';
import { fetchShopConfigCached } from '../../hooks/ecommerce/useShopOrdering';
import type { ShopStoreInfo } from '../../api/ecommerce.api';
import { commentaireClient } from '../../utils/commentaireClient';

/**
 * Une quantité d'ajustement se lit avec son unité : sans elle, « 1000 demandés,
 * 300 disponibles » pour un kilo de tomates se lit comme mille pièces.
 */
function formatAjustement(valeur: number, unite?: string): string {
  if (unite === 'grams') {
    return valeur >= 1000 ? `${(valeur / 1000).toFixed(valeur % 1000 === 0 ? 0 : 3)} kg` : `${valeur} g`;
  }
  if (unite === 'pieces') return `${valeur} pièce${valeur > 1 ? 's' : ''}`;
  return String(valeur);
}

export default function OrderConfirmationPage() {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const { customer, isLoading: isCustomerLoading } = useCustomer();

  const [order, setOrder] = useState<Order | null>(null);
  /**
   * Le stock a pu bouger entre le panier et l'enregistrement de la commande
   * (chemin « recharge puis commande » : c'est le serveur qui crée la commande
   * au moment du paiement, le client n'est plus devant l'écran). On sert ce qui
   * reste, et on le lui dit ici plutôt que de le laisser le découvrir au retrait.
   */
  /**
   * Coordonnées réelles du magasin et horaires, servis par /shop-config. Le
   * bloc de retrait affichait une adresse écrite en dur, à Bruxelles.
   */
  const [paiementEnCours, setPaiementEnCours] = useState(false);
  const [erreurPaiement, setErreurPaiement] = useState<string | null>(null);
  /**
   * Retour de Stripe (?payment=success) : la confirmation arrive par le webhook,
   * quelques secondes APRÈS que la cliente a été renvoyée ici. Lue trop tôt, la
   * commande est encore « en attente » — on annonçait « Paiement non finalisé »
   * et un bouton « Payer » qui répondait « déjà payée » (CMD-1791397043828,
   * 07/10/2026). On relit donc la commande quelques fois avant de conclure.
   */
  const retourStripe =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('payment') === 'success';
  const [attenteConfirmation, setAttenteConfirmation] = useState(false);

  const [magasin, setMagasin] = useState<ShopStoreInfo | null>(null);
  useEffect(() => {
    fetchShopConfigCached()
      .then((c) => setMagasin(c.store || null))
      .catch(() => setMagasin(null));
  }, []);

  const [ajustementsStock] = useState<{
    retirees: Array<{ produit: string; motif?: string }>;
    reduites: Array<{ produit: string; demande: number; servi: number; unite?: string }>;
  } | null>(() => {
    try {
      const brut = sessionStorage.getItem('order_stock_adjustments');
      if (!brut) return null;
      sessionStorage.removeItem('order_stock_adjustments');
      return JSON.parse(brut);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Attendre que le CustomerContext finisse de charger
    if (isCustomerLoading) {
      return;
    }

    // Si pas de client après chargement, rediriger vers login
    if (!customer) {
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/shop/login');
      return;
    }

    // Si pas d'ID de commande, rediriger vers la boutique
    if (!orderId) {
      navigate('/shop');
      return;
    }

    // Tout est OK, charger la commande
    loadOrder();
  }, [customer, orderId, isCustomerLoading, navigate]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const data = await ecommerceApi.orders.getOrderById(orderId!);
      setOrder(data);
      if (retourStripe && data && data.payment_status !== 'paid' && data.status !== 'cancelled') {
        void attendreConfirmation();
      }
    } catch (err: any) {
      console.error('Erreur chargement commande:', err);
      setError('Impossible de charger les détails de votre commande');
    } finally {
      setLoading(false);
    }
  };

  /** Relit la commande toutes les 2 s, 30 s au plus, jusqu'à la voir payée. */
  const attendreConfirmation = async () => {
    setAttenteConfirmation(true);
    try {
      for (let i = 0; i < 15; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const data = await ecommerceApi.orders.getOrderById(orderId!);
          setOrder(data);
          if (data?.payment_status === 'paid' || data?.payment_status === 'failed' || data?.status === 'cancelled') return;
        } catch {
          /* on réessaie au tour suivant */
        }
      }
    } finally {
      setAttenteConfirmation(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Chargement de votre commande...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Erreur</h1>
          <p className="text-gray-600 mb-6">{error || 'Commande introuvable'}</p>
          <button
            onClick={() => navigate('/shop')}
            className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700 transition-colors"
          >
            Retour à la boutique
          </button>
        </div>
      </div>
    );
  }

  /**
   * Commande carte non payée et non annulée : le paiement reste à faire.
   * Le serveur accepte de rouvrir une session tant que ces deux conditions
   * tiennent, et impose lui-même le total.
   */
  const paiementARegler =
    !!order &&
    order.payment_method === 'card' &&
    order.payment_status !== 'paid' &&
    order.status !== 'cancelled';

  const reprendrePaiement = async () => {
    if (!order || paiementEnCours) return;
    setPaiementEnCours(true);
    setErreurPaiement(null);
    try {
      const session = await ecommerceApi.stripe.createOrderPayment({
        order_id: order.id,
        amount_cents: Number(order.total_cents),
      });
      if (session.url) window.location.href = session.url;
      else setErreurPaiement('Impossible de démarrer le paiement. Réessayez dans un instant.');
    } catch (err: any) {
      const message: string = err?.response?.data?.message || '';
      // Le serveur sait déjà que c'est payé : relire la commande plutôt que
      // d'afficher une contradiction sous un bouton « Payer ».
      if (/déjà payée/i.test(message)) {
        await loadOrder();
        return;
      }
      setErreurPaiement(message || 'Impossible de démarrer le paiement. Réessayez dans un instant.');
    } finally {
      setPaiementEnCours(false);
    }
  };

  // Le lieu et l'horaire du JOUR de retrait choisi (marché le dimanche).
  const horaires = magasin?.opening_hours || [];
  const lieuDuJour = lieuDeRetrait(horaires, order?.delivery_date);
  const jourRetrait = order?.delivery_date
    ? new Date(`${String(order.delivery_date).slice(0, 10)}T12:00:00`).getDay()
    : null;
  const plageDuJour = jourRetrait === null ? null : horaires.find((h) => h.day === jourRetrait) || null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Une commande dont le paiement n'a pas abouti n'est pas confirmée : le
            dire, et donner le moyen de le reprendre. Le client atterrissait ici
            sur « Commande confirmée ! » après un paiement Stripe échoué ou
            abandonné — panier vidé, aucun écran pour payer, et la commande
            annulée 24 h plus tard. */}
        {paiementARegler && attenteConfirmation ? (
          <div className="bg-white border-2 border-green-200 rounded-2xl shadow-lg p-8 mb-8 print:hidden text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Confirmation du paiement…</h1>
            <p className="text-gray-700">
              Votre paiement de {(Number(order.total_cents) / 100).toFixed(2)} € est en cours de confirmation par
              la banque. Cela prend quelques secondes, ne fermez pas cette page.
            </p>
          </div>
        ) : paiementARegler ? (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl shadow-lg p-8 mb-8 print:hidden">
            <h1 className="text-3xl font-bold text-amber-900 text-center mb-2">
              Paiement non finalisé
            </h1>
            <p className="text-lg text-center text-amber-900">
              Votre commande <strong>{order.order_number}</strong> est enregistrée mais
              n'est pas encore payée — elle ne sera pas préparée en l'état, et aucun
              email de confirmation ne vous a été envoyé.
            </p>
            <p className="text-center text-amber-800 mt-2">
              Vous pouvez reprendre le paiement maintenant, ou depuis « Mes commandes ».
            </p>
            <div className="flex justify-center mt-5">
              <button
                type="button"
                onClick={reprendrePaiement}
                disabled={paiementEnCours}
                className="px-8 py-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-lg disabled:opacity-50"
              >
                {paiementEnCours ? 'Redirection…' : `Payer ${(Number(order.total_cents) / 100).toFixed(2)} €`}
              </button>
            </div>
            {erreurPaiement && (
              <p className="text-center text-red-700 mt-3">{erreurPaiement}</p>
            )}
          </div>
        ) : (
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl shadow-2xl p-8 mb-8 print:hidden animate-fade-in">
          <div className="flex items-center justify-center mb-4">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h1 className="text-4xl font-bold text-center mb-2">Commande confirmée !</h1>
          <p className="text-xl text-center text-green-50">
            Merci pour votre commande. Un email de confirmation a été envoyé à <strong>{customer?.email}</strong>.
          </p>
          <p className="text-base text-center text-green-100 mt-2">
            Vous recevrez un email lorsque votre commande sera prête.
          </p>
        </div>
        )}

        {(ajustementsStock?.retirees?.length || ajustementsStock?.reduites?.length) ? (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6 mb-8 print:hidden">
            <h2 className="text-xl font-bold text-amber-900 mb-2">Une partie de votre panier a changé</h2>
            <p className="text-amber-900 mb-3">
              Certains articles étaient partis en magasin au moment d'enregistrer votre commande.
              Vous n'êtes facturé que de ce que nous vous préparons.
            </p>
            <ul className="list-disc pl-5 space-y-1 text-amber-900">
              {ajustementsStock.retirees.map((r, i) => (
                <li key={`out-${i}`}>
                  <strong>{r.produit}</strong> : plus disponible, retiré de la commande.
                </li>
              ))}
              {ajustementsStock.reduites.map((r, i) => (
                <li key={`red-${i}`}>
                  <strong>{r.produit}</strong> : {formatAjustement(r.demande, r.unite)} demandé
                  {r.demande > 1 && !r.unite ? 's' : ''}, {formatAjustement(r.servi, r.unite)} préparé
                  {r.servi > 1 && !r.unite ? 's' : ''}.
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* Récapitulatif de commande - optimisé pour impression */}
        <div className="bg-white rounded-2xl shadow-lg print:shadow-none print:rounded-none">
          {/* En-tête - visible en impression */}
          <div className="border-b border-gray-200 p-8 print:p-3 print:pb-2">
            <div className="flex items-start justify-between mb-4 print:mb-2">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-1 print:text-lg print:mb-0">
                  Récapitulatif de commande
                </h2>
                <p className="text-gray-600 print:text-xs">Commande passée le {formatDate(order.created_at)}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600 mb-1 print:text-xs print:mb-0">Numéro de commande</div>
                <div className="text-3xl font-bold text-green-700 print:text-lg">
                  #{order.order_number}
                </div>
              </div>
            </div>

            {/* Informations client */}
            <div className="bg-gray-50 rounded-lg p-4 print:bg-white print:p-2 print:border print:border-gray-200">
              <div className="grid grid-cols-2 gap-4 print:gap-2">
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1 print:text-xs print:mb-0">Client</div>
                  <div className="font-bold print:text-sm">
                    {customer?.first_name} {customer?.last_name}
                  </div>
                  <div className="text-sm text-gray-600 print:text-xs">{customer?.email}</div>
                  {customer?.phone && (
                    <div className="text-sm text-gray-600 print:text-xs">{customer.phone}</div>
                  )}
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1 print:text-xs print:mb-0">Statut de paiement</div>
                  <div className={`inline-block px-3 py-1 rounded-full font-bold text-sm print:text-xs print:px-2 print:py-0.5 ${
                    order.payment_status === 'paid'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {order.payment_status === 'paid' ? '✓ Payé' : order.payment_method === 'cash' ? '🧾 À régler au retrait' : '⏳ En attente'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Détails de livraison/retrait */}
          <div className="border-b border-gray-200 p-8 print:p-3 print:pb-2">
            <h3 className="text-2xl font-bold text-gray-900 mb-3 print:text-base print:mb-1">
              {order.delivery_option === 'delivery' ? '🚚 Livraison' : '🏪 Retrait en magasin'}
            </h3>

            <div className="bg-blue-50 rounded-lg p-3 space-y-1 print:bg-white print:p-2 print:border print:border-gray-200 print:text-sm">
              {order.delivery_date && (
                <div className="flex justify-between print:text-xs">
                  <span className="font-medium text-gray-700">Date :</span>
                  <span className="font-bold">{formatJourRetrait(order.delivery_date)}</span>
                </div>
              )}
              {order.delivery_slot && (
                <div className="flex justify-between print:text-xs">
                  <span className="font-medium text-gray-700">Créneau horaire :</span>
                  <span className="font-bold">{order.delivery_slot}</span>
                </div>
              )}
              {order.delivery_address && order.delivery_option === 'delivery' && (
                <div className="mt-2 pt-2 border-t border-blue-200 print:mt-1 print:pt-1">
                  <div className="font-medium text-gray-700 mb-1 print:text-xs print:mb-0">Adresse de livraison :</div>
                  <div className="font-bold print:text-xs">{order.delivery_address.street}</div>
                  <div className="font-bold print:text-xs">
                    {order.delivery_address.postal_code} {order.delivery_address.city}
                  </div>
                  {order.delivery_address.additional_info && (
                    <div className="text-sm text-gray-600 mt-1 print:text-xs print:mt-0">
                      {order.delivery_address.additional_info}
                    </div>
                  )}
                </div>
              )}
              {order.delivery_option !== 'delivery' && (
                <div className="mt-2 pt-2 border-t border-blue-200 print:mt-1 print:pt-1">
                  {/* L'adresse était écrite en dur — « Rue de la boutique, 1000
                      Bruxelles » — sur le seul récapitulatif que le client peut
                      imprimer. Et le dimanche, le retrait n'est pas au magasin
                      mais au marché : le lieu suit le JOUR choisi. */}
                  <div className="font-medium text-gray-700 mb-1 print:text-xs print:mb-0">
                    {lieuDuJour ? 'Lieu de retrait :' : 'Adresse du magasin :'}
                  </div>
                  <div className="font-bold print:text-xs">{magasin?.name || 'Les Folies Maraîchères'}</div>
                  <div className="text-gray-700 print:text-xs">{lieuDuJour || magasin?.address || ''}</div>
                  {plageDuJour && (
                    <div className="text-gray-700 mt-1 print:text-xs print:mt-0 capitalize">
                      {formatPlageHoraire(plageDuJour)}
                    </div>
                  )}
                  {magasin?.phone && (
                    <div className="text-gray-600 text-sm mt-1 print:text-xs print:mt-0">
                      Tél. {magasin.phone}
                    </div>
                  )}
                </div>
              )}
            </div>

            {commentaireClient(order.notes) && (
              <div className="mt-3 p-3 bg-yellow-50 rounded-lg print:bg-white print:p-2 print:mt-2 print:border print:border-gray-200">
                <div className="font-medium text-gray-700 mb-1 print:text-xs print:mb-0">Commentaire :</div>
                <div className="text-gray-800 italic whitespace-pre-line print:text-xs">{commentaireClient(order.notes)}</div>
              </div>
            )}
          </div>

          {/* Articles commandés */}
          <div className="border-b border-gray-200 p-8 print:p-3 print:pb-2">
            <h3 className="text-2xl font-bold text-gray-900 mb-3 print:text-base print:mb-1">
              Articles commandés
            </h3>

            <div className="space-y-2 print:space-y-1">
              {order.lines.map((line) => (
                <div
                  key={line.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg print:bg-white print:p-1 print:border print:border-gray-200"
                >
                  <div className="flex-1">
                    <div className="font-bold text-gray-900 print:text-sm">{line.product_name}{(line as any).prep_options?.includes('coupé') ? ' · coupé' : ''}</div>
                    <div className="text-sm text-gray-600 print:text-xs">
                      Quantité : {line.quantity} × {(line.unit_price_cents / 100).toFixed(2)} €
                    </div>
                  </div>
                  <div className="text-xl font-bold text-gray-900 print:text-sm">
                    {(line.total_price_cents / 100).toFixed(2)} €
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totaux */}
          <div className="p-8 print:p-3">
            <div className="space-y-2 text-lg print:space-y-1 print:text-sm">
              <div className="flex justify-between text-gray-700 print:text-xs">
                <span>Sous-total :</span>
                <span className="font-medium">
                  {(Number(order.subtotal_cents) / 100).toFixed(2)} €
                </span>
              </div>

              {order.delivery_fee_cents > 0 && (
                <div className="flex justify-between text-gray-700 print:text-xs">
                  <span>Frais de {order.delivery_option === 'delivery' ? 'livraison' : 'service'} :</span>
                  <span className="font-medium">
                    {(Number(order.delivery_fee_cents) / 100).toFixed(2)} €
                  </span>
                </div>
              )}

              <div className="border-t-2 border-gray-300 pt-2 mt-2 print:pt-1 print:mt-1">
                <div className="flex justify-between text-3xl font-bold text-green-700 print:text-base">
                  <span>Total TTC :</span>
                  <span>{(Number(order.total_cents) / 100).toFixed(2)} €</span>
                </div>
              </div>
            </div>

            {/* Mode de paiement */}
            <div className="mt-4 p-3 bg-green-50 rounded-lg print:bg-white print:mt-2 print:p-2 print:border print:border-gray-200">
              <div className="flex items-center justify-between print:text-xs">
                <div>
                  <div className="text-sm text-gray-600 mb-1 print:text-xs print:mb-0">Mode de paiement</div>
                  <div className="font-bold text-gray-900 print:text-xs">
                    {order.payment_method === 'wallet' && '💳 Mes FOLIES'}
                    {order.payment_method === 'card' && '💳 Carte bancaire'}
                    {order.payment_method === 'cash' && '🧾 Au retrait (espèces, chèques repas, Bancontact)'}
                  </div>
                </div>
                <div className={`px-3 py-2 rounded-full font-bold text-sm print:text-xs print:px-2 print:py-0.5 ${
                  order.payment_status === 'paid'
                    ? 'bg-green-200 text-green-800'
                    : 'bg-yellow-200 text-yellow-800'
                }`}>
                  {order.payment_status === 'paid' ? 'Payé' : order.payment_method === 'cash' ? 'À régler au retrait' : 'À payer'}
                </div>
              </div>
            </div>
          </div>

          {/* Boutons d'action - masqués à l'impression */}
          <div className="border-t-2 border-gray-200 p-8 print:hidden">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={handlePrint}
                className="flex items-center justify-center gap-2 bg-blue-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Imprimer
              </button>

              <button
                onClick={() => navigate('/shop/orders')}
                className="flex items-center justify-center gap-2 bg-gray-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-gray-700 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                Mes commandes
              </button>

              <button
                onClick={() => navigate('/shop')}
                className="flex items-center justify-center gap-2 bg-green-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-green-700 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                Retour boutique
              </button>
            </div>

            <div className="mt-6 text-center text-gray-600">
              <p className="text-sm">
                Un email de confirmation a été envoyé à <span className="font-bold">{customer?.email}</span>
              </p>
              <p className="text-sm mt-2">
                Pour toute question, répondez directement à l'email ou contactez-nous à contact@foliesboutik.be
              </p>
            </div>
          </div>
        </div>

        {/* Footer pour impression */}
        <div className="hidden print:block mt-8 text-center text-sm text-gray-600">
          <div className="border-t-2 border-gray-300 pt-4">
            <p className="font-bold">Les Folies Maraîchères</p>
            <p>Elouges, Belgique</p>
            <p>Email: contact@foliesboutik.be</p>
            <p className="mt-2">Document imprimé le {new Date().toLocaleDateString('fr-FR')}</p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }

        @media print {
          body {
            background: white !important;
          }

          /* Cacher les éléments non imprimables */
          .print\\:hidden {
            display: none !important;
          }

          .print\\:block {
            display: block !important;
          }

          /* Styles d'impression compacts */
          .print\\:shadow-none {
            box-shadow: none !important;
          }

          .print\\:rounded-none {
            border-radius: 0 !important;
          }

          /* Paddings réduits */
          .print\\:p-3 {
            padding: 0.75rem !important;
          }

          .print\\:p-2 {
            padding: 0.5rem !important;
          }

          .print\\:p-1 {
            padding: 0.25rem !important;
          }

          .print\\:pb-2 {
            padding-bottom: 0.5rem !important;
          }

          .print\\:pt-1 {
            padding-top: 0.25rem !important;
          }

          .print\\:px-2 {
            padding-left: 0.5rem !important;
            padding-right: 0.5rem !important;
          }

          .print\\:py-0\\.5,
          .print\\:py-0 {
            padding-top: 0.125rem !important;
            padding-bottom: 0.125rem !important;
          }

          /* Marges réduites */
          .print\\:mb-0 {
            margin-bottom: 0 !important;
          }

          .print\\:mb-1 {
            margin-bottom: 0.25rem !important;
          }

          .print\\:mb-2 {
            margin-bottom: 0.5rem !important;
          }

          .print\\:mt-0 {
            margin-top: 0 !important;
          }

          .print\\:mt-1 {
            margin-top: 0.25rem !important;
          }

          .print\\:mt-2 {
            margin-top: 0.5rem !important;
          }

          /* Tailles de texte réduites */
          .print\\:text-xs {
            font-size: 0.75rem !important;
            line-height: 1rem !important;
          }

          .print\\:text-sm {
            font-size: 0.875rem !important;
            line-height: 1.25rem !important;
          }

          .print\\:text-base {
            font-size: 1rem !important;
            line-height: 1.5rem !important;
          }

          .print\\:text-lg {
            font-size: 1.125rem !important;
            line-height: 1.75rem !important;
          }

          /* Espacements réduits */
          .print\\:space-y-1 > * + * {
            margin-top: 0.25rem !important;
          }

          .print\\:gap-2 {
            gap: 0.5rem !important;
          }

          /* Bordures et backgrounds */
          .print\\:border {
            border: 1px solid #e5e7eb !important;
          }

          .print\\:border-gray-200 {
            border-color: #e5e7eb !important;
          }

          .print\\:bg-white {
            background-color: white !important;
          }

          @page {
            margin: 0.5cm;
          }
        }
      `}</style>
    </div>
  );
}
